import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setNavigation, resetNavigation } from "./store/navigationSlice";
import { resetNavbar } from "../navbar/navbarSlice";
import navigationConfig from "@/configs/navigationConfig";
import useUser from "@auth/useUser";
import { AUTH_PLAN_UPDATED_EVENT, getPlan } from "@/utils/localStorage";
import { HIDE_FOR_ADMIN } from "@/constants/navigationPermissions";

const NavigationLoader = () => {
  const dispatch = useDispatch();
  const { data: user, isGuest } = useUser();
  const [planCheck, setPlanCheck] = useState(0);
  const hasUserLoadedRef = useRef(false);
  const retryTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const lastUserIdRef = useRef<string | null>(null);
  const lastRoleRef = useRef<string | null>(null);
  const lastHasPlanRef = useRef<boolean | null>(null);
  const isInitializedRef = useRef(false);
  const isProcessingRef = useRef(false);

  // Memoize navigation update to prevent unnecessary re-renders
  const updateNavigation = useCallback((role: string, hasPlan: boolean, userId: string) => {
    // Prevent multiple simultaneous updates
    if (isProcessingRef.current) {
      return;
    }

    // Skip if navigation hasn't changed for the same user
    if (
      lastUserIdRef.current === userId &&
      lastRoleRef.current === role &&
      lastHasPlanRef.current === hasPlan
    ) {
      return;
    }
    
    isProcessingRef.current = true;
    lastUserIdRef.current = userId;
    lastRoleRef.current = role;
    lastHasPlanRef.current = hasPlan;
    
    const filteredNavigation = navigationConfig?.filter(
      (item) => !(role === "Admin" && HIDE_FOR_ADMIN.includes(item?.id))
    );

    dispatch(
      setNavigation({
        items: filteredNavigation,
        role: role as string,
        hasPlan,
      })
    );

    isProcessingRef.current = false;
  }, [dispatch]);

  // React to same-tab plan changes immediately
  useEffect(() => {
    const handlePlanUpdated = () => {
      setPlanCheck((prev) => prev + 1);
    };

    window.addEventListener(AUTH_PLAN_UPDATED_EVENT, handlePlanUpdated);
    return () => window.removeEventListener(AUTH_PLAN_UPDATED_EVENT, handlePlanUpdated);
  }, []);

  // Cleanup function for retry timeouts
  useEffect(() => {
    return () => {
      retryTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      retryTimeoutsRef.current = [];
    };
  }, []);

  // Set up retry mechanism when user first loads (to catch async localStorage setting)
  useEffect(() => {
    if (!user && !isGuest && !hasUserLoadedRef.current) {
      // Trigger re-check after a short delay to catch localStorage set asynchronously
      const timeout = setTimeout(() => {
        setPlanCheck((prev) => prev + 1);
      }, 100);
      retryTimeoutsRef.current.push(timeout);
    }
  }, [user, isGuest]);

  // Main effect to update navigation when user or plan check changes
  useEffect(() => {
    if (isGuest || !user) {
      if (isInitializedRef.current) {
        dispatch(resetNavigation());
        dispatch(resetNavbar());
        isInitializedRef.current = false;
      }
      return;
    }

    const role = user?.role;
    if (!role) {
      return;
    }

    // Determine if user has a plan by checking localStorage
    let hasPlan = true;
    if (role === "Applicant") {
      // Always read fresh localStorage value to ensure we have the latest
      const planData = getPlan();
      
      if (!planData) {
        hasPlan = false;
      } else {
        // Check if plan is valid (not empty object)
        if (Array.isArray(planData)) {
          hasPlan = planData.length > 0;
        } else if (planData && typeof planData === 'object') {
          const keys = Object.keys(planData);
          hasPlan = keys.length > 0 && planData.id != null && planData.id !== '';
        } else {
          hasPlan = false;
        }
      }
    }

    const userId = String(user.id || user.user_id || '');
    const userRole = Array.isArray(role) ? role[0] : role;
    updateNavigation(userRole, hasPlan, userId);
    hasUserLoadedRef.current = true;
    isInitializedRef.current = true;
  }, [user, isGuest, planCheck, updateNavigation, dispatch]);

  return null;
};

export default NavigationLoader;
